import {
  DocumentData,
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions,
  Timestamp,
  serverTimestamp,
  WithFieldValue,
  PartialWithFieldValue,
  SetOptions,
} from 'firebase/firestore';
import type {
  Tenant,
  Location,
  User,
  Lead,
  Member,
  Plan,
  Subscription,
  Invoice,
  Booking,
  CheckIn,
  Activity,
  Service,
  ServiceCategory,
  Unavailability,
  Appointment,
  ProviderSchedule,
} from './types';

// Helper to add timestamps to input data
function withTimestamps<T extends DocumentData>(data: T) {
  return {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
}

// Helper to add update timestamp
export function withUpdateTimestamp<T extends DocumentData>(data: T) {
  return {
    ...data,
    updatedAt: serverTimestamp(),
  };
}

// Generic converter factory
function createConverter<T extends DocumentData>(): FirestoreDataConverter<T> {
  return {
    toFirestore(
      modelObject: WithFieldValue<T> | PartialWithFieldValue<T>,
      options?: SetOptions
    ): DocumentData {
      const data = modelObject as DocumentData;
      
      // If it's a set operation without merge, add timestamps for new docs
      if (!options || !('merge' in options || 'mergeFields' in options)) {
        // Check if this is new data (no createdAt)
        if (!('createdAt' in data) || !data.createdAt) {
          return withTimestamps(data);
        }
      }
      
      // For updates, just add updatedAt
      if (options && ('merge' in options || 'mergeFields' in options)) {
        return withUpdateTimestamp(data);
      }
      
      return data;
    },
    fromFirestore(
      snapshot: QueryDocumentSnapshot<DocumentData>,
      options?: SnapshotOptions
    ): T {
      return snapshot.data(options) as T;
    },
  };
}

// Converters for each collection
export const tenantConverter = createConverter<Tenant>();
export const locationConverter = createConverter<Location>();
export const userConverter = createConverter<User>();
export const leadConverter = createConverter<Lead>();
export const memberConverter = createConverter<Member>();
export const planConverter = createConverter<Plan>();
export const subscriptionConverter = createConverter<Subscription>();
export const invoiceConverter = createConverter<Invoice>();
export const bookingConverter = createConverter<Booking>();
export const checkInConverter = createConverter<CheckIn>();
export const activityConverter = createConverter<Activity>();

// Appointment converter (replaces/extends existing bookingConverter)
export const appointmentConverter: FirestoreDataConverter<Appointment> = {
  toFirestore: (data) => {
    return {
      ...data,
      updatedAt: Timestamp.now(),
    };
  },
  fromFirestore: (snapshot) => {
    const data = snapshot.data();
    return {
      tenantId: data.tenantId,
      startTime: data.startTime?.toDate ? data.startTime : Timestamp.now(),
      endTime: data.endTime?.toDate ? data.endTime : undefined,
      serviceId: data.serviceId,
      providerId: data.providerId,
      customerType: data.customerType,
      customerId: data.customerId,
      guestInfo: data.guestInfo,
      locationId: data.locationId,
      status: data.status,
      paymentStatus: data.paymentStatus || 'not_required',
      stripePaymentIntentId: data.stripePaymentIntentId,
      stripeCheckoutSessionId: data.stripeCheckoutSessionId,
      paymentExpiresAt: data.paymentExpiresAt?.toDate ? data.paymentExpiresAt : undefined,
      emailReminderSent: data.emailReminderSent ?? false,
      reminderSentAt: data.reminderSentAt?.toDate ? data.reminderSentAt : undefined,
      notes: data.notes,
      customerNotes: data.customerNotes,
      source: data.source,
      publicAccessHash: data.publicAccessHash,
      googleCalendarEventId: data.googleCalendarEventId,
      createdAt: data.createdAt?.toDate ? data.createdAt : Timestamp.now(),
      updatedAt: data.updatedAt?.toDate ? data.updatedAt : Timestamp.now(),
    } as Appointment;
  },
};

// ProviderSchedule converter (handles complex nested types)
export const providerScheduleConverter: FirestoreDataConverter<ProviderSchedule> = {
  toFirestore: (data) => {
    return {
      ...data,
      updatedAt: Timestamp.now(),
    };
  },
  fromFirestore: (snapshot) => {
    const data = snapshot.data();
    return {
      tenantId: data.tenantId,
      providerId: data.providerId,
      workingPlan: data.workingPlan || {
        monday: null,
        tuesday: null,
        wednesday: null,
        thursday: null,
        friday: null,
        saturday: null,
        sunday: null,
      },
      exceptions: data.exceptions || {},
      timezone: data.timezone || 'America/New_York',
      createdAt: data.createdAt?.toDate ? data.createdAt : Timestamp.now(),
      updatedAt: data.updatedAt?.toDate ? data.updatedAt : Timestamp.now(),
    } as ProviderSchedule;
  },
};

// Service converter
export const serviceConverter: FirestoreDataConverter<Service> = {
  toFirestore: (data) => {
    return {
      ...data,
      createdAt: data.createdAt || Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
  },
  fromFirestore: (snapshot) => {
    const data = snapshot.data();
    return {
      tenantId: data.tenantId,
      name: data.name,
      description: data.description,
      duration: data.duration,
      price: data.price,
      currency: data.currency,
      paymentPolicy: data.paymentPolicy || (data.price && data.price > 0 ? 'pay_later' : 'not_required'),
      visibility: data.visibility,
      locationId: data.locationId,
      availabilitiesType: data.availabilitiesType,
      attendantsNumber: data.attendantsNumber,
      providerIds: data.providerIds || [],
      color: data.color,
      imageUrl: data.imageUrl,
      displayOrder: data.displayOrder || 0,
      isActive: data.isActive ?? true,
      categoryId: data.categoryId,
      createdAt: data.createdAt?.toDate ? data.createdAt : Timestamp.now(),
      updatedAt: data.updatedAt?.toDate ? data.updatedAt : Timestamp.now(),
    } as Service;
  },
};

// ServiceCategory converter
export const serviceCategoryConverter: FirestoreDataConverter<ServiceCategory> = {
  toFirestore: (data) => {
    return { ...data, updatedAt: Timestamp.now() };
  },
  fromFirestore: (snapshot) => {
    const data = snapshot.data();
    return {
      tenantId: data.tenantId,
      name: data.name,
      description: data.description,
      displayOrder: data.displayOrder || 0,
      isActive: data.isActive ?? true,
      createdAt: data.createdAt?.toDate ? data.createdAt : Timestamp.now(),
      updatedAt: data.updatedAt?.toDate ? data.updatedAt : Timestamp.now(),
    } as ServiceCategory;
  },
};

// Unavailability converter
export const unavailabilityConverter: FirestoreDataConverter<Unavailability> = {
  toFirestore: (data) => {
    return { ...data, updatedAt: Timestamp.now() };
  },
  fromFirestore: (snapshot) => {
    const data = snapshot.data();
    return {
      tenantId: data.tenantId,
      providerId: data.providerId,
      startTime: data.startTime?.toDate ? data.startTime : Timestamp.now(),
      endTime: data.endTime?.toDate ? data.endTime : Timestamp.now(),
      reason: data.reason,
      createdBy: data.createdBy,
      createdAt: data.createdAt?.toDate ? data.createdAt : Timestamp.now(),
      updatedAt: data.updatedAt?.toDate ? data.updatedAt : Timestamp.now(),
    } as Unavailability;
  },
};

// Helper functions for working with Firestore data

/**
 * Convert a Date to Firestore Timestamp
 */
export function dateToTimestamp(date: Date): Timestamp {
  return Timestamp.fromDate(date);
}

/**
 * Convert Firestore Timestamp to Date
 */
export function timestampToDate(timestamp: Timestamp): Date {
  return timestamp.toDate();
}

/**
 * Get current server timestamp
 */
export function getServerTimestamp() {
  return serverTimestamp();
}

/**
 * Prepare data for creation (adds timestamps)
 */
export function prepareCreate<T extends DocumentData>(data: T) {
  return withTimestamps(data);
}

/**
 * Prepare data for update (adds updated timestamp)
 */
export function prepareUpdate<T extends DocumentData>(data: T) {
  return withUpdateTimestamp(data);
}
